import type { ReactNode } from "react";

/**
 * The shared shape of every terminal error surface: 404, 403, 500, app crash.
 *
 * Branded chrome-less layout, matching the login page and the 404 it was lifted
 * from: an error is reached from outside the app as often as from inside it, so
 * a surface cannot assume the authenticated header is around it. One component
 * so the whole set reads as one application rather than four dead ends.
 *
 * Presentational only — each variant supplies its own copy and its own ways
 * out, and nothing here knows what threw.
 */
export function ErrorSurface({
  code,
  title,
  message,
  detail,
  actions,
}: {
  /** The HTTP status shown above the headline. Omitted for stateful errors (a crash has no status). */
  code?: string;
  title: string;
  message: string;
  /** Extra explanation below the standard message — a service's own words, or dev-only detail. */
  detail?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="bg-lennar-grey-50 flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="border-lennar-grey-200 w-full max-w-md space-y-6 rounded-xl border bg-white p-8 text-center shadow-sm">
        <div className="flex justify-center">
          <img src="/lennar-logo.svg" alt="Lennar" className="h-6 w-auto" />
        </div>

        <div className="space-y-2">
          {code && (
            <p className="text-lennar-blue-500 text-sm font-semibold tracking-widest">{code}</p>
          )}
          <h1 className="text-lennar-blue-600 text-3xl font-medium tracking-tight">{title}</h1>
          <p className="text-lennar-grey-600 text-base font-light">{message}</p>
          {detail}
        </div>

        {actions && <div className="space-y-3">{actions}</div>}
      </div>
    </div>
  );
}
